import { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Divider,
  Box, Button, TextField, IconButton, Paper, Chip, Alert,
  Tabs, Tab, Select, MenuItem, InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import Page from '../components/Page';
import Iconify from '../components/Iconify';

const API = axios.create({
  baseURL: 'https://cpccbe.org/api'
});

export default function MembersAdmin() {
  const [tab, setTab] = useState(0);

  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);

  const [originalTypes, setOriginalTypes] = useState([]);
  const [originalMembers, setOriginalMembers] = useState([]);

  const [selectedType, setSelectedType] = useState('');

  const [isEditingTypes, setIsEditingTypes] = useState(false);
  const [isEditingMembers, setIsEditingMembers] = useState(false);

  const [search, setSearch] = useState('');

  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ================= FETCH =================
  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      fetchMembers(selectedType);
      setSearch(''); // reset search when type changes
    }
  }, [selectedType]);

  const fetchTypes = async () => {
    try {
      const res = await API.get('/members/types');
      setTypes(res.data.data || []);
      setOriginalTypes(res.data.data || []);
    } catch {
      setErrorMessage('Failed to load types ❌');
    }
  };

  const fetchMembers = async (typeId) => {
    try {
      const res = await API.get(`/members/by-type/${typeId}`);
      setMembers(res.data.data || []);
      setOriginalMembers(res.data.data || []);
    } catch {
      setErrorMessage('Failed to load members ❌');
    }
  };

  // ================= FILTER =================
  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase())
  );

  // ================= TYPE =================
  const addType = () => {
    setTypes(prev => [...prev, { id: Date.now(), name: '' }]);
  };

  const updateType = (id, value) => {
    setTypes(prev =>
      prev.map(t => (t._id || t.id) === id ? { ...t, name: value } : t)
    );
  };

  const deleteType = async (type) => {
    try {
      if (type._id) {
        await API.delete(`/members/type/${type._id}`);
      }
      setTypes(prev =>
        prev.filter(t => (t._id || t.id) !== (type._id || type.id))
      );
      setMessage('Type deleted ✅');
    } catch {
      setErrorMessage('Delete failed ❌');
    }
  };

  const saveTypes = async () => {
    const requests = types.map(t => {
      if (!t.name.trim()) return null;

      if (t._id) {
        return API.put(`/members/type/${t._id}`, { name: t.name });
      }
      return API.post(`/members/type`, { name: t.name });
    });

    await Promise.all(requests);
    setMessage('Member Types saved ✅');
    fetchTypes();
  };

  // ================= MEMBER =================
  const addMember = () => {
    setMembers(prev => [...prev, { id: Date.now(), name: '' }]);
  };

  const updateMember = (id, value) => {
    setMembers(prev =>
      prev.map(m => (m._id || m.id) === id ? { ...m, name: value } : m)
    );
  };

  const deleteMember = async (member) => {
    try {
      if (member._id) {
        await API.delete(`/members/member/${member._id}`);
      }
      setMembers(prev =>
        prev.filter(m => (m._id || m.id) !== (member._id || member.id))
      );
      setMessage('Member deleted ✅');
    } catch {
      setErrorMessage('Delete failed ❌');
    }
  };

  const saveMembers = async () => {
    const requests = members.map(m => {
      if (!m.name.trim()) return null;

      if (m._id) {
        return API.put(`/members/member/${m._id}`, {
          name: m.name,
          memberType: selectedType
        });
      }
      return API.post(`/members/member`, {
        name: m.name,
        memberType: selectedType
      });
    });

    await Promise.all(requests);
    setMessage('Members saved ✅');
    fetchMembers(selectedType);
  };

  return (
    <Page title="Members">
      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h4">Members Configuration</Typography>

          {/* TYPES */}
          {tab === 0 && (
            !isEditingTypes ? (
              <Button variant="contained" onClick={() => setIsEditingTypes(true)}>
                Edit Types
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={() => {
                  setTypes(originalTypes);
                  setIsEditingTypes(false);
                }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={async () => {
                  await saveTypes();
                  setIsEditingTypes(false);
                }}>
                  Save Types
                </Button>
              </Box>
            )
          )}

          {/* MEMBERS */}
          {tab === 1 && (
            !isEditingMembers ? (
              <Button variant="contained" onClick={() => setIsEditingMembers(true)}>
                Edit Members
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={() => {
                  setMembers(originalMembers);
                  setIsEditingMembers(false);
                }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={async () => {
                  await saveMembers();
                  setIsEditingMembers(false);
                }}>
                  Save Members
                </Button>
              </Box>
            )
          )}
        </Box>

        {/* ALERT */}
        {message && <Alert sx={{ mb: 2 }}>{message}</Alert>}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* TABS */}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Member Types" />
          <Tab label="Members" />
        </Tabs>

        {/* ================= TYPES ================= */}
        {tab === 0 && (
          <Card>
            <CardContent>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Member Types ({types.length})</Typography>

                {isEditingTypes && (
                  <Button startIcon={<AddIcon />} onClick={addType}>
                    Add Type
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {types.map((type, index) => (
                  <Grid item xs={12} key={type._id || type.id}>
                    <Paper sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip label={index + 1} color="primary" />

                        <TextField
                          value={type.name}
                          fullWidth
                          disabled={!isEditingTypes}
                          onChange={(e) =>
                            updateType(type._id || type.id, e.target.value)
                          }
                        />

                        {isEditingTypes && (
                          <IconButton color="error" onClick={() => deleteType(type)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </CardContent>
          </Card>
        )}

        {/* ================= MEMBERS ================= */}
        {tab === 1 && (
          <Card>
            <CardContent>

              {/* SELECT TYPE */}
              <Select
                fullWidth
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Member Type</MenuItem>
                {types.map(t => (
                  <MenuItem key={t._id} value={t._id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>

              {/* SEARCH */}
              {selectedType && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search member..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    )
                  }}
                />
              )}

              {selectedType && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6">
                      Members ({filteredMembers.length})
                    </Typography>

                    {isEditingMembers && (
                      <Button startIcon={<AddIcon />} onClick={addMember}>
                        Add Member
                      </Button>
                    )}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* EMPTY SEARCH */}
                  {filteredMembers.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Typography color="text.secondary">
                        No members found 🔍
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      {filteredMembers.map((member, index) => (
                        <Grid item xs={12} key={member._id || member.id}>
                          <Paper sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Chip label={index + 1} color="primary" />

                              <TextField
                                value={member.name}
                                fullWidth
                                disabled={!isEditingMembers}
                                onChange={(e) =>
                                  updateMember(member._id || member.id, e.target.value)
                                }
                              />

                              {isEditingMembers && (
                                <IconButton color="error" onClick={() => deleteMember(member)}>
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}

            </CardContent>
          </Card>
        )}

      </Container>
    </Page>
  );
}